import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Divider,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { DepartmentVatSyncResult, DepartmentVatMismatch } from '../../hooks/fiscalDevices/ingenico';
import { fixDepartmentMismatches } from '../../hooks/fiscalDevices/ingenico';

interface DepartmentVatMismatchModalProps {
  open: boolean;
  onClose: () => void;
  syncResult: DepartmentVatSyncResult | null;
  onFixed?: () => void;
}

const DepartmentVatMismatchModal: React.FC<DepartmentVatMismatchModalProps> = ({
  open,
  onClose,
  syncResult,
  onFixed,
}) => {
  const [isFixing, setIsFixing] = useState(false);
  const [fixResult, setFixResult] = useState<'success' | 'error' | null>(null);
  const [fixError, setFixError] = useState('');
  const [password, setPassword] = useState('');

  if (!syncResult) return null;

  const handleFixMismatches = async () => {
    if (!password.trim()) return;

    setIsFixing(true);
    setFixResult(null);
    setFixError('');

    try {
      const result = await fixDepartmentMismatches(syncResult.mismatches, password);
      if (result.success) {
        setFixResult('success');
        onFixed?.();
      } else {
        setFixResult('error');
        setFixError(result.error || 'Bilinmeyen hata');
      }
    } catch (err: any) {
      setFixResult('error');
      setFixError(err.message || 'Beklenmeyen hata');
    } finally {
      setIsFixing(false);
    }
  };

  const handleClose = () => {
    setFixResult(null);
    setFixError('');
    setPassword('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: fixResult === 'success' ? '#e8f5e9' : '#fff3e0',
          borderBottom: '1px solid',
          borderColor: fixResult === 'success' ? '#a5d6a7' : '#ffe0b2',
        }}
      >
        {fixResult === 'success' ? (
          <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
        ) : (
          <WarningAmberIcon sx={{ color: '#e65100', fontSize: 28 }} />
        )}
        <Typography component="span" variant="h6" fontWeight={600}>
          {fixResult === 'success'
            ? 'Departman-KDV Eşleşmesi Güncellendi'
            : 'Departman-KDV Uyumsuzluğu Tespit Edildi'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {fixResult === 'success' ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Yazarkasadaki departman-KDV eşleşmeleri başarıyla güncellendi. Artık satışa devam edebilirsiniz.
          </Alert>
        ) : (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Yazarkasadaki bazı departman numaralarının KDV oranları, ürünlerinizin KDV oranlarıyla eşleşmiyor.
              Bu durum fiş basımında hatalı KDV hesaplamasına neden olabilir.
            </Alert>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, mt: 2 }}>
              Cihazdaki Departman-KDV Tablosu
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 200 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Dept No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Departman Adı</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>KDV Index</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>KDV Oranı</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncResult.departments.map((dept) => {
                    const hasMismatch = syncResult.mismatches.some(m => m.deptIndex === dept.deptIndex);
                    return (
                      <TableRow
                        key={dept.deptIndex}
                        sx={hasMismatch ? { bgcolor: '#fff8e1' } : undefined}
                      >
                        <TableCell>{dept.deptIndex}</TableCell>
                        <TableCell>{dept.deptName}</TableCell>
                        <TableCell>{dept.taxIndex}</TableCell>
                        <TableCell>
                          %{dept.taxRatePercent}
                          {hasMismatch && (
                            <Chip
                              label="Uyumsuz"
                              size="small"
                              color="warning"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} color="error" sx={{ mb: 1 }}>
              Uyumsuz Departmanlar ve Etkilenen Ürünler
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 250 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Dept No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cihazdaki KDV</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Beklenen KDV</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Etkilenen Ürünler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncResult.mismatches.map((m: DepartmentVatMismatch) => (
                    <TableRow key={m.deptIndex}>
                      <TableCell>
                        <Chip label={`Dept ${m.deptIndex}`} size="small" color="error" variant="outlined" />
                      </TableCell>
                      <TableCell>%{m.deviceVatPercent} (index: {m.deviceTaxIndex})</TableCell>
                      <TableCell sx={{ color: '#2e7d32', fontWeight: 600 }}>
                        %{m.expectedVatPercent} (index: {m.expectedTaxIndex})
                      </TableCell>
                      <TableCell>
                        {m.affectedProducts.slice(0, 5).join(', ')}
                        {m.affectedProducts.length > 5 && ` +${m.affectedProducts.length - 5} ürün daha`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Seçenek 1:</strong> "Cihazı Güncelle" ile yazarkasadaki departman-KDV eşleşmesini otomatik düzeltebilirsiniz (supervisor şifresi gerekir).
              <br />
              <strong>Seçenek 2:</strong> "Kapat" ile bu uyarıyı kapatıp, ürün departman numaralarını GTS'den manuel olarak güncelleyebilirsiniz. Ödeme durduruldu.
            </Alert>

            {fixResult === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Güncelleme başarısız: {fixError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small"
                type="password"
                label="Supervisor Şifresi"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ minWidth: 200 }}
                disabled={isFixing}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={isFixing ? <CircularProgress size={18} color="inherit" /> : <BuildIcon />}
                onClick={handleFixMismatches}
                disabled={isFixing || !password.trim()}
              >
                {isFixing ? 'Güncelleniyor...' : 'Cihazı Güncelle'}
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          type="button"
          variant={fixResult === 'success' ? 'contained' : 'contained'}
          color={fixResult === 'success' ? 'success' : 'primary'}
          startIcon={fixResult === 'success' ? <CheckCircleIcon /> : <CloseIcon />}
          onClick={handleClose}
          autoFocus
        >
          {fixResult === 'success' ? 'Tamam' : 'Kapat'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DepartmentVatMismatchModal;
